import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { MenuGroup, Section } from '@atlaskit/menu';
import { Box } from '@atlaskit/primitives/compiled';
import { cssMap } from '@atlaskit/css';

import { useShareToEntities } from '@confluence/share/entry-points/useShareToEntities';
import { type PageMode } from '@confluence/copy-content-link';
import { PAGE_MODE } from '@confluence/share/entry-points/useCopyContentLink';
import type { FlagsStateContainer } from '@confluence/flags';
import { withFlags } from '@confluence/flags';

import type { PresenceContentMode, PresenceContentType } from '../presenceTypes';

import type { SuggestedUser } from './SuggestedCollaboratorsDialog';
import { SuggestedCollaboratorsDialogMenuItem } from './SuggestedCollaboratorsDialogMenuItem';

const menuStyles = cssMap({
	scrollableMenuStyles: {
		// height for 5 suggested users and 12px of padding from Section component, 6 before and 6 after the list
		maxHeight: '252px',
		overflowY: 'auto',
	},
});

const i18n = defineMessages({
	sharedFlag: {
		id: 'team-presence.menu.empty-state.shared.flag',
		defaultMessage: 'Content shared',
		description: 'Text shown in a flag when a user clicks the share button.',
	},
});

export type SuggestedCollaboratorsDialogShareSuggestionMenuProps = {
	contentId: string;
	contentMode: PresenceContentMode;
	contentType: PresenceContentType;
	pageMode: PageMode;
	suggestedUsers: SuggestedUser[];
	flags: FlagsStateContainer;
};

export const SuggestedCollaboratorsDialogMenuComponent = ({
	contentId,
	contentType,
	contentMode,
	pageMode,
	suggestedUsers,
	flags,
}: SuggestedCollaboratorsDialogShareSuggestionMenuProps) => {
	const { formatMessage } = useIntl();

	const isEditMode = pageMode === 'edit';
	const { shareToEntities } = useShareToEntities(
		contentId,
		'suggestedCollaboratorsDialog',
		isEditMode ? PAGE_MODE.EDIT : PAGE_MODE.VIEW,
	);

	return (
		<MenuGroup>
			<Box xcss={menuStyles.scrollableMenuStyles}>
				<Section>
					{suggestedUsers.map((suggestedUser) => {
						const handleShareClick = async (): Promise<void> => {
							const result = await shareToEntities(
								[{ id: suggestedUser.id, type: 'user' }],
								isEditMode,
							);

							if (result.success) {
								void flags.showSuccessCircleFlag({
									id: 'team-presence-share-completed-flag',
									title: formatMessage(i18n.sharedFlag),
									isAutoDismiss: true,
								});
							}
						};

						return (
							<SuggestedCollaboratorsDialogMenuItem
								key={suggestedUser.id}
								suggestedUser={suggestedUser}
								onHandleShare={handleShareClick}
								contentId={contentId}
								contentType={contentType}
								contentMode={contentMode}
							/>
						);
					})}
				</Section>
			</Box>
		</MenuGroup>
	);
};

export const SuggestedCollaboratorsDialogMenu = withFlags(
	SuggestedCollaboratorsDialogMenuComponent,
);
